import { useTranslation } from 'react-i18next';

export default function AboutSection() {
  const { t } = useTranslation();

  const scrollToCases = () => {
    const el = document.getElementById('cases');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="about" className="relative py-24 bg-gradient-to-b from-[#0a0118] to-[#16213e] overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #00ff41 0px, #00ff41 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #00ff41 0px, #00ff41 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>

      <div className="relative z-10 w-full px-6 mb-16 flex flex-col items-center" style={{ animation: 'sloganFloat 4s ease-in-out infinite' }}>
        <div className="flex items-center gap-4 w-full max-w-4xl mb-6">
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, #00ff4155, #00ff41cc)' }} />
          <span className="text-[#00ff41] font-['JetBrains_Mono'] text-xs tracking-[0.3em] opacity-60">MOTTO</span>
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(to left, transparent, #00ff4155, #00ff41cc)' }} />
        </div>

        <div className="text-center max-w-4xl">
          <div
            className="font-['Space_Grotesk'] font-black tracking-tight leading-none mb-2"
            style={{
              fontSize: 'clamp(2.8rem, 7vw, 5.5rem)',
              background: 'linear-gradient(90deg, #00ff41 0%, #00d9ff 50%, #00ff41 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'sloganShimmer 4s linear infinite',
            }}
          >
            {t('about.sloganMain')}
          </div>
          <div
            className="font-['Space_Grotesk'] font-light tracking-[0.15em] text-white/80"
            style={{ fontSize: 'clamp(1.1rem, 2.8vw, 1.8rem)', letterSpacing: '0.18em' }}
          >
            {t('about.sloganPre')}
            <span
              className="font-semibold mx-1"
              style={{ color: '#00d9ff', textShadow: '0 0 12px #00d9ffaa, 0 0 28px #00d9ff55' }}
            >
              {t('about.sloganHighlight')}
            </span>
            {t('about.sloganPost')}
            <span className="text-[#00ff41] ml-1" style={{ textShadow: '0 0 10px #00ff41' }}>。</span>
          </div>
        </div>

        <div className="flex items-center gap-4 w-full max-w-4xl mt-6">
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, #00d9ff33, #00d9ff88)' }} />
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00ff41]" style={{ boxShadow: '0 0 6px #00ff41', animation: 'sloganPulse 1.8s ease-in-out infinite' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-[#00d9ff]" style={{ boxShadow: '0 0 6px #00d9ff', animation: 'sloganPulse 1.8s ease-in-out infinite 0.6s' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff006e]" style={{ boxShadow: '0 0 6px #ff006e', animation: 'sloganPulse 1.8s ease-in-out infinite 1.2s' }} />
          </div>
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(to left, transparent, #00d9ff33, #00d9ff88)' }} />
        </div>
      </div>

      <style>{`
        @keyframes sloganFloat { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-7px); } }
        @keyframes sloganPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.3; transform: scale(0.6); } }
        @keyframes sloganShimmer { 0% { background-position: 0% center; } 100% { background-position: 200% center; } }
      `}</style>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="mb-16">
          <div className="inline-block px-4 py-2 bg-[#00ff41]/10 border border-[#00ff41] text-[#00ff41] text-xs font-['JetBrains_Mono'] font-semibold mb-6"
            style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
          >
            {t('about.sectionTag')}
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white font-['Space_Grotesk'] mb-4">
            <span className="block text-2xl md:text-3xl text-gray-400 mb-2">{t('about.titleLine1')}</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff41] to-[#00d9ff]">
              {t('about.titleLine2')}
            </span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-[#1a0b2e]/50 backdrop-blur-sm border-l-4 border-[#00ff41] p-6 hover:bg-[#1a0b2e]/70 transition-all duration-300"
            style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
          >
            <div className="text-[#00ff41] font-['JetBrains_Mono'] text-xs mb-3">{t('about.card1Label')}</div>
            <p className="text-gray-300 leading-relaxed font-['Space_Grotesk']">
              {t('about.card1Text1')}<strong className="text-white">{t('about.card1Brand')}</strong>{t('about.card1Text2')}<strong className="text-[#00ff41]">{t('about.card1Highlight')}</strong>{t('about.card1Text3')}
            </p>
          </div>

          <div className="bg-[#1a0b2e]/50 backdrop-blur-sm border-l-4 border-[#00d9ff] p-6 hover:bg-[#1a0b2e]/70 transition-all duration-300"
            style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
          >
            <div className="text-[#00d9ff] font-['JetBrains_Mono'] text-xs mb-3">{t('about.card2Label')}</div>
            <p className="text-gray-300 leading-relaxed font-['Space_Grotesk']">
              {t('about.card2Text1')}<strong className="text-white">{t('about.card2Highlight1')}</strong>{t('about.card2Text2')}<strong className="text-[#00d9ff]">{t('about.card2Highlight2')}</strong>{t('about.card2Text3')}
            </p>
          </div>

          <div className="bg-[#1a0b2e]/50 backdrop-blur-sm border-l-4 border-[#ff006e] p-6 hover:bg-[#1a0b2e]/70 transition-all duration-300 flex flex-col"
            style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
          >
            <div className="text-[#ff006e] font-['JetBrains_Mono'] text-xs mb-3">{t('about.card3Label')}</div>
            <p className="text-gray-300 leading-relaxed font-['Space_Grotesk'] flex-1">
              {t('about.card3Text1')}<strong className="text-white">{t('about.card3Highlight1')}</strong>{t('about.card3Text2')}<strong className="text-[#ff006e]">{t('about.card3Highlight2')}</strong>{t('about.card3Text3')}
            </p>
            <button
              onClick={scrollToCases}
              className="mt-5 w-full flex items-center justify-between px-4 py-2.5 font-['JetBrains_Mono'] text-xs font-semibold tracking-widest text-[#ff006e] border border-[#ff006e]/40 hover:border-[#ff006e] hover:bg-[#ff006e]/10 transition-all duration-300 group whitespace-nowrap cursor-pointer"
              style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
            >
              <span>{t('about.card3Cta')}</span>
              <span className="flex items-center gap-1 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          <div className="md:col-span-2 relative overflow-hidden group"
            style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))' }}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/d5eb7696e2cf0f1df4886ae8037f6f26.jpg"
              alt="AI Studio"
              className="w-full h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
              <div className="text-white font-['JetBrains_Mono']">
                <div className="text-[#00ff41] text-xs mb-1">{t('about.workspaceLabel')}</div>
                <div className="text-sm">{t('about.workspaceDesc')}</div>
              </div>
            </div>
            <div className="absolute inset-0 border-2 border-[#00ff41]/0 group-hover:border-[#00ff41]/50 transition-all duration-300"></div>
          </div>

          <div className="md:col-span-3 relative overflow-hidden group"
            style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))' }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20AI%20neural%20network%20processing%20video%20data%2C%20flowing%20digital%20particles%20forming%20video%20frames%2C%20glowing%20data%20streams%20in%20neon%20green%20and%20cyan%20colors%2C%20futuristic%20technology%20concept%20art%2C%20dark%20background%20with%20luminous%20effects%2C%20representing%20AI%20video%20generation%20process%2C%20cinematic%20wide%20angle%20composition&width=1200&height=600&seq=about-process-2&orientation=landscape"
              alt="AI Process"
              className="w-full h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
              <div className="text-white font-['JetBrains_Mono']">
                <div className="text-[#00d9ff] text-xs mb-1">{t('about.techLabel')}</div>
                <div className="text-sm">{t('about.techDesc')}</div>
              </div>
            </div>
            <div className="absolute inset-0 border-2 border-[#00d9ff]/0 group-hover:border-[#00d9ff]/50 transition-all duration-300"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
